import React from "react";
import { useI18n } from "@/lib/i18n";

/**
 * The three-step client journey, animated with CSS only — no Lottie payload,
 * no JS timers, and a fixed 12-node DOM footprint.
 */
const STEPS = [
  { key: "tsf_s1", frames: ["1", "2", "3"] },
  { key: "tsf_s2", frames: null },
  { key: "tsf_s3", frames: null },
];

const SelectFrames = () => (
  <div className="grid grid-cols-3 gap-1.5" aria-hidden="true">
    {[0, 1, 2, 3, 4, 5].map((i) => (
      <span key={i}
        className={`tsf-tile block aspect-square border border-[#2A2A2A] bg-[#141414] tsf-tile-${i % 3}`} />
    ))}
  </div>
);

const NoteLines = () => (
  <div className="space-y-2" aria-hidden="true">
    {[0, 1, 2].map((i) => (
      <span key={i} className={`tsf-line block h-1.5 bg-[#2A2A2A] tsf-line-${i}`} />
    ))}
  </div>
);

const PrintCounters = () => (
  <div className="space-y-2.5" aria-hidden="true">
    {["4x6", "12x18", "16x24"].map((s, i) => (
      <div key={s} className="flex items-center gap-3">
        <span className="font-mono-accent text-[9px] text-[#707070] w-12 shrink-0">{s}</span>
        <span className="flex-1 h-1.5 bg-[#1E1E1E] overflow-hidden">
          <span className={`tsf-bar block h-full bg-[#D4AF37] tsf-bar-${i}`} />
        </span>
      </div>
    ))}
  </div>
);

const VISUALS = [SelectFrames, NoteLines, PrintCounters];

export const ThreeStepFlow = () => {
  const { t } = useI18n();

  return (
    <div data-testid="three-step-flow">
      <p className="eyebrow mb-4">{t("tsf_eyebrow")}</p>
      <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight max-w-2xl">{t("tsf_title")}</h2>
      <p className="mt-5 text-sm sm:text-base text-[#A8A8A8] max-w-xl leading-relaxed">{t("tsf_body")}</p>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
        {STEPS.map((s, i) => {
          const Visual = VISUALS[i];
          return (
            <div key={s.key} data-testid={`tsf-step-${i + 1}`}
              className="bg-[#0B0B0B] p-8 lg:p-9 hover:bg-[#121212] transition-colors duration-500">
              <div className="flex items-baseline justify-between gap-4">
                <span className="font-mono-accent text-xs text-[#D4AF37]">0{i + 1}</span>
                <span className="font-mono-accent text-[9px] tracking-[0.16em] uppercase text-[#5A5A5A]">
                  {t("tsf_step")} {i + 1}/3
                </span>
              </div>
              <div className="mt-8 mb-8 h-24 flex items-center">
                <div className="w-full max-w-[168px]"><Visual /></div>
              </div>
              <h3 className="text-xl tracking-tight">{t(`${s.key}_t`)}</h3>
              <p className="mt-3 text-sm text-[#A8A8A8] leading-relaxed">{t(`${s.key}_b`)}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};
